/**
 * Insights Panel Component
 *
 * Displays AI-generated insights with proper formatting
 */

'use client';

import React from 'react';
import {
  LightBulbIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

export interface Insight {
  type: 'positive' | 'negative' | 'neutral';
  title: string;
  description: string;
}

interface InsightsPanelProps {
  insights: Insight[];
  provider?: string;
  dataScope?: {
    club: boolean;
    valuations: boolean;
    playerSales: boolean;
  };
}

export function InsightsPanel({ insights, provider, dataScope }: InsightsPanelProps) {
  if (!insights || insights.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Insights</h3>
        </div>

        {provider && (
          <span className="rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {provider}
          </span>
        )}
      </div>

      {/* Data Scope Indicator */}
      {dataScope && (
        <div className="mb-4 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <InformationCircleIcon className="h-4 w-4" />
          <span>
            Based on:{' '}
            {[
              dataScope.club && 'Club Info',
              dataScope.valuations && 'Valuations',
              dataScope.playerSales && 'Player Sales',
            ]
              .filter(Boolean)
              .join(', ')}
          </span>
        </div>
      )}

      {/* Insights List */}
      <div className="space-y-4">
        {insights.map((insight, index) => (
          <InsightCard key={index} insight={insight} />
        ))}
      </div>
    </div>
  );
}

function InsightCard({ insight }: { insight: Insight }) {
  const getIcon = () => {
    switch (insight.type) {
      case 'positive':
        return <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />;
      case 'negative':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />;
      default:
        return <LightBulbIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
    }
  };

  const getColorClasses = () => {
    switch (insight.type) {
      case 'positive':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'negative':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      default:
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
    }
  };

  return (
    <div className={`rounded-lg border p-4 ${getColorClasses()}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{getIcon()}</div>
        <div className="flex-1">
          <h4 className="mb-1 font-semibold text-gray-900 dark:text-white">{insight.title}</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300">{insight.description}</p>
        </div>
      </div>
    </div>
  );
}
